/**
 * Ce qu'on retient d'une partie — les statistiques locales du § 12.
 *
 * ## Pourquoi on ne les lit pas à la fin de la partie
 *
 * Aucune des choses qu'on veut compter ne survit dans l'état final. `manche.poses`
 * est remis à zéro à chaque manche, `evenements` est borné et décoratif (§ 3.2), et
 * le journal est plafonné. Un 421 réussi à la manche 3 n'existe plus nulle part à la
 * manche 12 : il faut le compter au passage.
 *
 * ## La même méthode que les annonces
 *
 * On compare **deux états successifs**, et jamais un flux d'événements. Le § 6.4 est
 * formel : Colyseus ne conserve que la dernière valeur d'une propriété par intervalle
 * de patch, et saute le handshake à la reconnexion. Des compteurs bâtis sur les
 * événements seraient faux **en ligne seulement** — c'est-à-dire là où on les
 * vérifierait le moins.
 *
 * ## Le corollaire : chaque mesure doit résister à la répétition
 *
 * Le même état peut être présenté plusieurs fois (React rerend, Colyseus repousse).
 * Toutes les détections d'ici sont donc des **transitions** — « ce jet n'était pas
 * posé et l'est maintenant », « la manche n'avait pas de rampeau et en a un » —, et
 * jamais des états — « il y a un rampeau ». C'est la différence entre trois 421 dans
 * la partie et trois 421 par seconde.
 */
import { evaluerMain } from "./combinaisons.js";
/** Score de la nénette (221), la plus faible du jeu. */
const NENETTE = "221";
export function statistiquesVides() {
    return {
        parties: 0, victoires: 0, tournees: 0, manches: 0,
        quatreCentVingtEtUn: 0, nenettes: 0, secs: 0, rampeaux: 0,
        jetonsRamasses: 0, coupsEnCharge: 0, coupsEnDecharge: 0,
        meilleurCoup: null, meilleurScore: 0,
    };
}
/**
 * Ce que la transition `avant` → `apres` ajoute au compteur, du point de vue de
 * `monSiege`.
 *
 * Rend toujours un objet neuf : les statistiques se transportent dans un état React
 * et dans une progression sauvegardée, où une mutation en place passerait inaperçue
 * jusqu'à ce qu'un rendu manque.
 */
export function suivre(stats, avant, apres, monSiege) {
    let out = { ...stats };
    // --- les jets que je viens de poser -----------------------------------------
    const manche = apres.manche;
    if (manche) {
        // ⚠️ Le siège ne suffit pas à reconnaître un jet déjà compté : les `poses` sont
        // remises à zéro à chaque manche, et le siège 0 repose à la manche suivante. Si
        // la manche a changé, TOUT est neuf — sans quoi le 421 de la manche 2 serait pris
        // pour celui de la manche 1, et jamais compté.
        const memeManche = avant.manche?.numero === manche.numero;
        const dejaPoses = new Set(memeManche ? Object.keys(avant.manche?.poses ?? {}) : []);
        for (const [siege, pose] of Object.entries(manche.poses)) {
            if (dejaPoses.has(siege) || Number(siege) !== monSiege)
                continue;
            const combinaison = evaluerMain(pose.main);
            if (combinaison.cle === "421")
                out.quatreCentVingtEtUn++;
            if (combinaison.cle === NENETTE)
                out.nenettes++;
            if (combinaison.score > out.meilleurScore) {
                out.meilleurScore = combinaison.score;
                out.meilleurCoup = combinaison.cle;
            }
        }
        // --- le sec : compté à celui qui l'impose, c'est-à-dire l'ouvreur ----------
        if (avant.manche?.lancersMax === null && manche.lancersMax === 1
            && manche.ouvreur === monSiege) {
            out.secs++;
        }
        // --- le rampeau : à son ouverture, et une seule fois -----------------------
        if (!avant.manche?.rampeau && manche.rampeau?.participants.includes(monSiege)) {
            out.rampeaux++;
        }
        // Une manche qui change de numéro est une manche jouée.
        const precedente = avant.manche?.numero ?? manche.numero;
        if (manche.numero > precedente)
            out.manches += manche.numero - precedente;
    }
    // --- les jetons qui arrivent -------------------------------------------------
    const avantMoi = avant.joueurs[monSiege];
    const apresMoi = apres.joueurs[monSiege];
    if (avantMoi && apresMoi) {
        const delta = apresMoi.jetons - avantMoi.jetons;
        if (delta > 0) {
            out.jetonsRamasses += delta;
            // La phase lue est celle d'AVANT : le transfert récompense le tour qui vient
            // de se jouer, et la bascule en décharge peut être publiée dans le même état.
            if (avant.phase === "DECHARGE")
                out.coupsEnDecharge++;
            else
                out.coupsEnCharge++;
        }
    }
    // --- la fin --------------------------------------------------------------
    if (avant.perdant === null && apres.perdant !== null) {
        out.parties++;
        if (apres.perdant === monSiege)
            out.tournees++;
        else
            out.victoires++;
        // La dernière manche s'est bien jouée : sans cette ligne elle manquerait,
        // puisque `manche` retombe à null sans passer par un numéro de plus.
        if (avant.manche)
            out.manches++;
    }
    return out;
}
/** Somme de deux relevés — le cumul d'une session dans le total de toujours. */
export function additionner(a, b) {
    const meilleur = b.meilleurScore > a.meilleurScore ? b : a;
    return {
        parties: a.parties + b.parties,
        victoires: a.victoires + b.victoires,
        tournees: a.tournees + b.tournees,
        manches: a.manches + b.manches,
        quatreCentVingtEtUn: a.quatreCentVingtEtUn + b.quatreCentVingtEtUn,
        nenettes: a.nenettes + b.nenettes,
        secs: a.secs + b.secs,
        rampeaux: a.rampeaux + b.rampeaux,
        jetonsRamasses: a.jetonsRamasses + b.jetonsRamasses,
        coupsEnCharge: a.coupsEnCharge + b.coupsEnCharge,
        coupsEnDecharge: a.coupsEnDecharge + b.coupsEnDecharge,
        meilleurCoup: meilleur.meilleurCoup,
        meilleurScore: meilleur.meilleurScore,
    };
}
//# sourceMappingURL=statistiques.js.map